<template>
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90 transition-opacity"
    @click="$emit('close')"
  >
    <div class="relative w-full max-w-5xl px-4">
      <!-- Close button -->
      <button
        @click="$emit('close')"
        class="absolute top-4 right-4 text-white bg-gray-800 rounded-full p-2 hover:bg-gray-700 transition-colors hover:text-blue-400"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>

      <!-- Image container -->
      <div class="max-h-screen flex items-center justify-center" @click.stop>
        <img
          :src="getImageUrl(currentImage)"
          :alt="product?.name"
          class="max-h-[80vh] max-w-full object-contain shadow-2xl"
        />
      </div>

      <!-- Navigation arrows for lightbox -->
      <div
        v-if="product && product.pictures && product.pictures.length > 1"
        class="absolute inset-x-0 top-1/2 transform -translate-y-1/2 flex justify-between px-4"
        @click.stop
      >
        <button
          @click.stop="navigateToPreviousImage"
          class="bg-gray-800 hover:bg-gray-700 rounded-full p-3 text-white focus:outline-none transition-colors hover:text-blue-400"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M15 18l-6-6 6-6"></path>
          </svg>
        </button>

        <button
          @click.stop="navigateToNextImage"
          class="bg-gray-800 hover:bg-gray-700 rounded-full p-3 text-white focus:outline-none transition-colors hover:text-blue-400"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M9 18l6-6-6-6"></path>
          </svg>
        </button>
      </div>

      <!-- Image counter -->
      <div
        class="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-gray-800 bg-opacity-80 px-4 py-2 rounded-full text-white shadow-md"
        @click.stop
      >
        {{ currentImageIndex + 1 }} / {{ product?.pictures?.length }}
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, PropType } from 'vue';
import { Product } from '@/composables/useProductDetail';
import { getImageUrl as getImageUrlUtil } from '@/utils/imageUtils'; // DODATO

export default defineComponent({
  name: 'ProductLightbox',
  props: {
    product: {
      type: Object as PropType<Product>,
      required: true,
    },
    currentImage: {
      type: String,
      required: true,
    },
    // apiUrl prop se više ne koristi, ali ostaviti za kompatibilnost
    apiUrl: {
      type: String,
      required: false,
      default: '',
    },
  },
  emits: ['close', 'update:current-image'],
  setup(props, { emit }) {
    const currentImageIndex = computed(() => {
      if (!props.product || !props.currentImage) return 0;

      const index = props.product.pictures.findIndex(
        (pic) => pic.path === props.currentImage
      );
      return index >= 0 ? index : 0;
    });

    // ZAMENJENA FUNKCIJA - koristi novi imageUtils
    const getImageUrl = (imagePath: string): string => {
      return getImageUrlUtil(imagePath);
    };

    const navigateToPreviousImage = () => {
      if (!props.product || !props.product.pictures.length) return;

      const currentIndex = currentImageIndex.value;
      const newIndex =
        currentIndex <= 0
          ? props.product.pictures.length - 1 // Go to last image if at first
          : currentIndex - 1; // Otherwise go to previous

      emit('update:current-image', props.product.pictures[newIndex].path);
    };

    const navigateToNextImage = () => {
      if (!props.product || !props.product.pictures.length) return;

      const currentIndex = currentImageIndex.value;
      const newIndex =
        currentIndex >= props.product.pictures.length - 1
          ? 0 // Go back to first image if at last
          : currentIndex + 1; // Otherwise go to next

      emit('update:current-image', props.product.pictures[newIndex].path);
    };

    return {
      currentImageIndex,
      getImageUrl,
      navigateToPreviousImage,
      navigateToNextImage,
    };
  },
});
</script>
